import React from 'react';
import { t } from '@lingui/core/macro';

import SimplifiedPriceColumn from '@rover/components/src/cards/SearchResultCard/PriceColumn/SimplifiedPriceColumn';
import DropdownSmall from '@rover/icons/svg/dropdown-small.svg';
import { Button } from '@rover/kibble/core';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { SitterProfileVersionOptions } from '../../constants';
import { useRevampCta } from '../../hooks/useRevampCta';
import { useRevampCtaCommon } from '../../hooks/useRevampCtaCommon';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import AdditionalRatesSection from '../ServiceSection/AdditionalRatesSection';

import { SitterProfileSeeAllRatesClicked } from './analyticsEvents';
import { CTAPropsCommon } from './RevampServicesCta';

type Props = {
  handleOpenSheet: () => void;
};

const RevampMobileCtaBarCommon = ({
  handleOpenSheet,
  priceTransparencyLevel,
  language,
  petCount,
  petType,
}: Props & Omit<CTAPropsCommon, 'bookingFeePercentage'>): JSX.Element => {
  const {
    sitterData,
    serviceType,
    selectedService,
    serviceDisplayName,
    serviceIsAway,
    isBrightHorizonsUser,
  } = useRevampCta();
  const { rate, price, isPetCareModes } = useRevampCtaCommon(
    priceTransparencyLevel,
    petType,
    petCount,
    selectedService
  );

  const { showSittersRates } = useRevampProfileContext();
  const priceInCredits = rate?.priceInCredits != null ? Number(rate.priceInCredits) : null;
  const shouldShowPriceInCredits =
    isBrightHorizonsUser && !showSittersRates && priceInCredits != null;
  return (
    <>
      <Button
        variant="link"
        size="default"
        icon={DropdownSmall}
        iconAlignment="right"
        onPress={handleOpenSheet}
        aria-label={
          serviceDisplayName ? t`Select a service: ${serviceDisplayName}` : t`Select a service`
        }
        padding="0x"
        sx={{
          color: serviceIsAway ? 'text.disabled' : 'text.primary',
        }}
      >
        {serviceDisplayName ?? t`Select a service`}
      </Button>

      {rate && price && (
        <AdditionalRatesSection
          services={sitterData.services}
          petType={petType}
          petCount={petCount}
          showSittersRates={showSittersRates}
          isSitterInPetCareModesExperiment={isPetCareModes}
          isCtaModal
          isRevampVersion
          renderTrigger={(toggleModal) => (
            <SimplifiedPriceColumn
              variant="cta-mobile"
              currencyCode={rate.pricePerUnitObj.currencyIso}
              priceTransparencyLevel={priceTransparencyLevel}
              language={language}
              price={price}
              priceInCredits={priceInCredits}
              shouldShowPriceInCredits={shouldShowPriceInCredits}
              showFromText={!shouldShowPriceInCredits}
              unitAsSentence={rate.unitAsSentence}
              isBHUser={isBrightHorizonsUser}
              onPriceClick={() => {
                emitAnalyticsEvent(
                  new SitterProfileSeeAllRatesClicked({
                    serviceSlug: serviceType ?? '',
                    pageVariant: SitterProfileVersionOptions.REVAMP,
                  })
                );
                toggleModal();
              }}
            />
          )}
        />
      )}
    </>
  );
};

export default RevampMobileCtaBarCommon;
